import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import api, { API, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Check, Sparkles, Infinity as InfinityIcon, ShieldCheck, Loader2, Mail, ArrowLeft, ExternalLink, Gift } from "lucide-react";

const TIER_META = {
  tier_3d:       { tagline: "Trial pendek",     badge: null,       popular: false },
  tier_7d:       { tagline: "Cuba seminggu",    badge: null,       popular: false },
  tier_30d:      { tagline: "Pengguna aktif",   badge: "POPULAR",  popular: true  },
  tier_3m:       { tagline: "Penjimatan terbaik", badge: "JIMAT",  popular: false },
  tier_lifetime: { tagline: "Akses tanpa had",  badge: "TERBAIK",  popular: false },
};

// Public-or-authenticated pricing page.
// Uses raw axios for /payments/tiers (public) and falls back to entered email when no token.
export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [promo, setPromo] = useState(null); // { active, label, bonuses:[{tier,tier_label,base_days,bonus_days,total_days}] }
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const [email, setEmail] = useState("");
  const [discount, setDiscount] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null); // {url, tier}

  const isLoggedIn = !!localStorage.getItem("participant_token");

  useEffect(() => {
    (async () => {
      try {
        const [tiersResp, promoResp] = await Promise.all([
          axios.get(`${API}/payments/tiers`),
          axios.get(`${API}/payments/promo`).catch(() => ({ data: { active: false } })),
        ]);
        setTiers(tiersResp.data);
        setPromo(promoResp.data?.active ? promoResp.data : null);
        if (isLoggedIn) {
          try {
            const meResp = await api.get("/participant/me");
            setMe(meResp.data);
            setEmail(meResp.data?.email || "");
          } catch (_) { /* token might be invalid; allow anonymous */ }
        } else {
          const last = localStorage.getItem("participant_email");
          if (last) setEmail(last);
        }
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Check discount eligibility when email changes
  useEffect(() => {
    const raw = isLoggedIn ? (me?.email || "") : (email || "");
    const e = String(raw).trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setDiscount(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/payments/check-discount`, { params: { email: e } });
        setDiscount(data.eligible ? data : null);
      } catch (_) {
        setDiscount(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [email, me, isLoggedIn]);

  const buy = async (tier) => {
    setError("");
    const targetEmail = isLoggedIn ? me?.email : email.trim().toLowerCase();
    if (!targetEmail) {
      setError("Sila masukkan emel anda dahulu.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(targetEmail)) {
      setError("Format emel tidak sah.");
      return;
    }
    setBuying(tier.id);
    try {
      const headers = isLoggedIn
        ? { Authorization: `Bearer ${localStorage.getItem("participant_token")}` }
        : {};
      const { data } = await axios.post(
        `${API}/payments/create`,
        { tier: tier.id, email: targetEmail },
        { headers },
      );
      // remember email for next page
      localStorage.setItem("participant_email", targetEmail);

      const url = data.payment_url;
      // 1. Try opening in a new tab/window (works outside iframes)
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (opened) {
        // Show fallback link too in case the new tab is blocked silently
        setPendingPayment({ url, tier: tier.label });
        setBuying(null);
        return;
      }
      // 2. Try to break out of any iframe
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = url;
          return;
        }
      } catch (_) {
        // cross-origin frame blocked top access — fall through to manual link
      }
      // 3. Last resort: navigate the current window (may be blocked inside iframe)
      try {
        window.location.assign(url);
      } catch (_) {
        // ignore
      }
      // Always show manual link as fallback
      setPendingPayment({ url, tier: tier.label });
      setBuying(null);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBuying(null);
    }
  };

  const features = [
    "Akses kepada Generator AI",
    "4 model AI (Gemini, GPT-4o, GPT-5.2, Claude)",
    "21+ jenis poster",
    "Multi-gambar upload",
    "Auto-suggestion (AI Cadang)",
    "Sejarah prompt tanpa had",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header for non-logged-in users */}
      {!isLoggedIn && (
        <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="pricing-brand-link">
              <Brand size="sm" showTagline={false} />
            </Link>
            <Link
              to="/login"
              className="flex items-center gap-2 text-sm text-neutral-400 hover:text-amber-400 transition-colors"
              data-testid="pricing-login-link"
            >
              <ArrowLeft size={14} /> Log Masuk
            </Link>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">
            Beli / Lanjut Akses
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3">Pilih Pakej Anda</h1>
          <p className="text-sm text-neutral-400 max-w-xl mx-auto">
            Bayar sekali, terus diaktifkan. Sistem akan auto-update tarikh tamat akses anda dalam beberapa saat selepas bayaran berjaya.
          </p>
          {me && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/30 bg-amber-500/5 text-xs font-mono">
              <ShieldCheck size={12} className="text-amber-400" />
              <span className="text-neutral-300">{me.email}</span>
              <span className="text-neutral-500">·</span>
              <span className="text-amber-400">
                {me.expiry_date ? `Tamat ${me.expiry_date}` : "∞ Selamanya"}
              </span>
            </div>
          )}
        </div>

        {/* Email input for anonymous users */}
        {!isLoggedIn && (
          <div className="max-w-xl mx-auto mb-8 bg-[#0F0F0F] border border-amber-500/20 rounded-xl p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-500/80 font-mono mb-3">
              <Mail size={14} /> Emel Anda
            </div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="anda@example.com"
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm focus:border-amber-500/40 focus:outline-none"
              data-testid="anonymous-email-input"
            />
            <p className="text-[11px] text-neutral-500 mt-2">
              Selepas bayaran berjaya, emel ini akan auto-didaftarkan dalam sistem. Anda terus boleh log masuk untuk mula menjana prompt.
            </p>
          </div>
        )}

        {/* PROMOSI PELANCARAN — hero banner */}
        {promo && promo.bonuses && promo.bonuses.length > 0 && (
          <div
            className="max-w-3xl mx-auto mb-8 relative overflow-hidden rounded-2xl border border-amber-400/50 bg-gradient-to-br from-amber-500/25 via-amber-400/10 to-transparent p-6 shadow-xl shadow-amber-500/10"
            data-testid="launch-promo-banner"
          >
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-amber-400/10 blur-2xl pointer-events-none" />
            <div className="relative flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-xl bg-amber-500 text-black flex items-center justify-center shadow-lg shadow-amber-500/30 shrink-0">
                <Gift size={26} />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-amber-300 mb-1 font-mono animate-pulse">
                  🎉 {promo.label}
                </div>
                {promo.bonuses.map((b) => (
                  <div key={b.tier} className="text-white">
                    <div className="font-display text-xl sm:text-2xl font-bold leading-tight">
                      Langgan <span className="gold-text">{b.tier_label}</span> — <span className="text-amber-300">Percuma {Math.round(b.bonus_days / 30)} Bulan</span>!
                    </div>
                    <div className="text-sm text-neutral-300 mt-1">
                      Bayar untuk <span className="text-white font-mono">{b.base_days} hari</span>, dapat akses penuh selama{" "}
                      <span className="text-amber-300 font-mono font-bold">{b.total_days} hari</span>{" "}
                      <span className="text-neutral-500">(~{Math.round(b.total_days / 30)} bulan)</span>. Untuk masa terhad sahaja.
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Discount banner */}
        {discount && (
          <div className="max-w-xl mx-auto mb-6 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-amber-500/15 border border-amber-400/40 rounded-xl p-4 text-center" data-testid="discount-banner">
            <div className="text-sm font-display font-bold text-amber-300">
              🎉 Diskaun {discount.discount_pct}% untuk pembelian pertama!
            </div>
            <div className="text-xs text-neutral-400 mt-1">
              Pakej{" "}
              <span className="text-amber-400 font-mono">
                {tiers.find((t) => t.id === discount.tier)?.label || discount.tier}
              </span>{" "}
              hanya{" "}
              <span className="text-amber-300 font-bold">RM{discount.discounted_price_rm}</span>{" "}
              <span className="line-through text-neutral-500">RM{discount.original_price_rm}</span>{" "}
              · Tawaran sekali sahaja
            </div>
          </div>
        )}

        {error && (
          <div className="max-w-xl mx-auto mb-6 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3" data-testid="pricing-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Memuatkan…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="tier-grid">
            {tiers.map((t) => {
              const meta = TIER_META[t.id] || {};
              const isLifetime = t.days === null;
              const hasDiscount = discount && discount.tier === t.id;
              const hasPromo = t.promo_active && t.bonus_days > 0;
              const finalPrice = hasDiscount ? discount.discounted_price_rm : t.price_rm;
              return (
                <div
                  key={t.id}
                  className={`relative bg-[#0F0F0F] border rounded-xl p-5 flex flex-col transition-all ${
                    hasPromo
                      ? "border-amber-400/70 shadow-xl shadow-amber-500/25"
                      : hasDiscount
                      ? "border-amber-400/70 shadow-lg shadow-amber-500/20"
                      : meta.popular
                      ? "border-amber-500/50 shadow-lg shadow-amber-500/10"
                      : "border-white/10 hover:border-amber-500/30"
                  }`}
                  data-testid={`tier-card-${t.id}`}
                >
                  {hasPromo ? (
                    <div
                      className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-mono font-bold bg-gradient-to-r from-amber-500 to-amber-300 text-black animate-pulse flex items-center gap-1"
                      data-testid={`promo-badge-${t.id}`}
                    >
                      <Gift size={10} /> +{Math.round(t.bonus_days / 30)} BULAN FREE
                    </div>
                  ) : hasDiscount ? (
                    <div className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-mono font-bold bg-gradient-to-r from-amber-500 to-amber-300 text-black animate-pulse">
                      -{discount.discount_pct}% PERTAMA SAHAJA
                    </div>
                  ) : meta.badge ? (
                    <div className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-mono font-bold bg-amber-500 text-black">
                      {meta.badge}
                    </div>
                  ) : null}
                  <div className="font-display text-xl font-bold">{t.label}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{meta.tagline}</div>
                  <div className="my-4 flex items-baseline gap-2 flex-wrap">
                    <span className="text-3xl font-display font-bold gold-text">RM{finalPrice}</span>
                    {hasDiscount && (
                      <span className="text-sm text-neutral-500 line-through font-mono">RM{t.price_rm}</span>
                    )}
                  </div>
                  <div className="text-xs mb-4">
                    {isLifetime ? (
                      <div className="flex items-center gap-1.5 text-neutral-400">
                        <InfinityIcon size={14} className="text-amber-400" />
                        <span>Akses tanpa tarikh tamat</span>
                      </div>
                    ) : hasPromo ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-amber-300 font-semibold">
                          <Gift size={12} className="text-amber-400" />
                          <span>+{t.total_days} hari akses (~{Math.round(t.total_days / 30)} bulan)</span>
                        </div>
                        <div className="text-[10px] text-neutral-500 font-mono ml-5">
                          {t.days} hari + <span className="text-amber-400">{t.bonus_days} hari PERCUMA</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-neutral-400">
                        <Sparkles size={12} className="text-amber-400" />
                        <span>+{t.days} hari akses</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => buy(t)}
                    disabled={buying === t.id}
                    className={`mt-auto w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold transition-all text-sm ${
                      hasPromo || hasDiscount || meta.popular || isLifetime
                        ? "bg-amber-500 text-black hover:bg-amber-400"
                        : "border border-white/15 text-white hover:bg-white/5"
                    } disabled:opacity-50`}
                    data-testid={`buy-${t.id}`}
                  >
                    {buying === t.id ? <Loader2 size={14} className="animate-spin" /> : null}
                    {buying === t.id ? "Memproses…" : "Beli Sekarang"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 max-w-3xl mx-auto bg-[#0F0F0F] border border-white/10 rounded-xl p-6">
          <div className="text-xs font-mono uppercase tracking-wider text-amber-500/80 mb-3">
            Semua pakej termasuk
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
                <Check size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 text-xs text-neutral-500 leading-relaxed">
            Pembayaran dikendalikan secara selamat oleh <span className="text-amber-400">BayarCash</span> · FPX & DuitNow. Selepas bayar, emel anda akan auto-didaftarkan dalam sistem dan tarikh tamat akses dilanjutkan secara automatik.
          </div>
        </div>

        {isLoggedIn && (
          <div className="mt-8 text-center">
            <Link
              to="/transactions"
              className="text-sm text-neutral-400 hover:text-amber-400 transition-colors"
              data-testid="view-transactions-link"
            >
              → Lihat Sejarah Transaksi
            </Link>
          </div>
        )}
      </div>

      {/* Manual continue modal — appears as a fallback when redirect/popup is blocked */}
      {pendingPayment && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pending-payment-modal">
          <div className="bg-[#101010] border border-amber-500/40 rounded-xl p-6 w-full max-w-md text-center shadow-2xl">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
              <ExternalLink className="text-amber-400" size={24} />
            </div>
            <h3 className="font-display text-2xl font-bold mb-2">Buka Halaman Bayaran</h3>
            <p className="text-sm text-neutral-400 mb-5">
              Klik butang di bawah untuk teruskan ke BayarCash dan selesaikan bayaran <span className="text-amber-400">{pendingPayment.tier}</span>.
              Jika tab baru tidak terbuka, klik manual untuk buka.
            </p>
            <a
              href={pendingPayment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-4 py-3 rounded-md bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors text-sm mb-3"
              data-testid="manual-payment-link"
              onClick={() => setPendingPayment(null)}
            >
              Teruskan ke BayarCash →
            </a>
            <button
              onClick={() => setPendingPayment(null)}
              className="text-xs text-neutral-500 hover:text-neutral-300"
              data-testid="cancel-payment-btn"
            >
              Batal
            </button>
            <div className="mt-5 pt-4 border-t border-white/10 text-[10px] text-neutral-600 font-mono break-all">
              {pendingPayment.url}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
